import { yupResolver } from '@hookform/resolvers/yup';
import { Contact, PlanDataWhoToCallOptions } from 'api/client';
import Button from 'components/button';
import { H4 } from 'components/headings';
import TextInput from 'components/text-input';
import React, { FC } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useUnmount } from 'react-use';
import { NavigatePlan } from 'types/base';
import { settings } from 'utils/config';
import { navigateWithDirection } from 'utils/helpers';
import * as yup from 'yup';
import { ReactComponent as PhoneIcon } from '../../../assets/icons/icon-phone.svg';
import { ReactComponent as PlusIcon } from '../../../assets/icons/icon-plus.svg';
import { ReactComponent as TrashIcon } from '../../../assets/icons/icon-trash.svg';
import FormControls from '../form-controls';
import {
  ButtonContentWrapper,
  ButtonWrapper,
  CardInnerWrapper,
  CardOuterWrapper,
  CardTitleWrapper,
  Description,
  Fieldset,
  IconButton,
  QuestionWrapper,
  Title,
} from '../question.styles';

const FormSchema = yup
  .object()
  .shape({
    contacts: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().nullable(),
          phoneNumber: yup.string().nullable(),
        })
      )
      .defined(),
  })
  .defined();

interface Props {
  whoToCall?: PlanDataWhoToCallOptions;
  onSetWhoToCall: (whoToCall: PlanDataWhoToCallOptions) => void;
  navigate?: NavigatePlan;
}

type FormData = {
  contacts: Contact[];
};

const defaultValuesNew: FormData = {
  contacts: [{ name: '', phoneNumber: '' }],
};

const WhoToCall: FC<Props> = (props) => {
  const {
    register,
    handleSubmit,
    watch,
    control,
    formState: { isSubmitting, isDirty },
  } = useForm<FormData>({
    defaultValues: props.whoToCall ? props.whoToCall : defaultValuesNew,
    resolver: yupResolver(FormSchema),
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contacts',
  });

  /*
    The purpose of this function is to remove any blank contact fields when the page is navigated away from.
    The first contact always needs to be preserved however, even if empty so that a contact card is rendered
    for the user to fill out.
    If the first contact is empty, but another contact exists, this function will shuffle the completed contact
    to the first index and remove the other iteration of itself.
  */
  const removeEmptyContacts = (whoToCall: PlanDataWhoToCallOptions) => {
    // Remove any empty contacts unless they are the first one
    let newContacts = whoToCall.contacts.filter(
      (item, index) => item.name || item.phoneNumber || index === 0
    );

    // If the first contact is empty, but there is another contact, remove the first contact.
    if (
      newContacts.length > 1 &&
      !newContacts[0].name &&
      !newContacts[0].phoneNumber
    ) {
      newContacts.shift();
    }

    return newContacts;
  };

  const onSubmit = handleSubmit((whoToCall, event) => {
    props.onSetWhoToCall({
      contacts: removeEmptyContacts(whoToCall),
    });
    navigateWithDirection(props.navigate, event);
  });

  const formData = watch();
  useUnmount(() => {
    if (!isSubmitting && isDirty) {
      const whoToCall = { ...formData };
      props.onSetWhoToCall({
        contacts: removeEmptyContacts(whoToCall),
      });
    }
  });

  return (
    <form noValidate onSubmit={onSubmit}>
      <QuestionWrapper>
        <Title>Who will we call?</Title>

        <Description>
          Choose a relative or a friend to call when you are leaving and when
          you arrive at your location, so they know you are safe.
        </Description>

        {fields.map((field, index) => (
          <CardOuterWrapper key={field.id}>
            {index !== 0 && (
              <IconButton
                type='button'
                aria-label='delete contact'
                onClick={() => {
                  remove(index);
                }}
              >
                <TrashIcon aria-hidden />
              </IconButton>
            )}
            <CardInnerWrapper>
              <Fieldset>
                <CardTitleWrapper>
                  <PhoneIcon />
                  <H4 as='legend'>Emergency Contact {index + 1}</H4>
                </CardTitleWrapper>
                <TextInput
                  label='Name:'
                  type='text'
                  placeholder='e.g. Aunt Grace'
                  name={`contacts[${index}].name`}
                  ref={register()}
                  defaultValue={field.name}
                />
                <TextInput
                  label='Phone number:'
                  type='tel'
                  name={`contacts[${index}].phoneNumber`}
                  ref={register()}
                  defaultValue={field.phoneNumber}
                />
              </Fieldset>
            </CardInnerWrapper>
          </CardOuterWrapper>
        ))}

        <ButtonWrapper>
          <Button
            variant='dark'
            disabled={fields.length >= settings.maxNumOfItems}
            onClick={() => append({})}
          >
            <ButtonContentWrapper>
              <PlusIcon />
              Add another contact
            </ButtonContentWrapper>
          </Button>
        </ButtonWrapper>
      </QuestionWrapper>

      <FormControls />
    </form>
  );
};

export default WhoToCall;
